import { useMemo } from "react";
import { cn } from "@apex/shared";
import { useWidget } from "../../state";
import { Button } from "../ui/Button";

export interface SuccessViewProps {
  className?: string;
}

/** Confetti positions (x,y offsets and colors) */
const CONFETTI = [
  { x: "-30px", y: "-35px", color: "hsl(var(--apex-primary))" },
  { x: "30px", y: "-30px", color: "hsl(var(--apex-accent-cta))" },
  { x: "-40px", y: "5px", color: "hsl(var(--apex-success))" },
  { x: "40px", y: "0px", color: "hsl(var(--apex-warning))" },
  { x: "-20px", y: "30px", color: "hsl(var(--apex-primary))" },
  { x: "25px", y: "35px", color: "hsl(var(--apex-accent-cta))" },
  { x: "0px", y: "-42px", color: "hsl(var(--apex-success))" },
  { x: "0px", y: "40px", color: "hsl(var(--apex-warning))" },
];

/** External-link icon for the "Visit our website" CTA */
function ExternalLinkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

export function SuccessView({ className }: SuccessViewProps) {
  const { state, theme, reset } = useWidget();
  const { quoteResult, selectedRootItem, quoteForm } = state;

  // Timeline copy (#539). Sourced from `theme.confirmationCopy` (now backed
  // by WidgetThemeConfig.ConfirmationCopy on the backend, #541) with a
  // built-in 3-line fallback when unset.
  const timelineSteps = theme.confirmationCopy;

  const websiteUrl = theme.tenantWebsiteUrl;

  // Capture the submission timestamp once, on first render of the
  // success view, so the displayed time doesn't drift while the user
  // sits on the page.
  const submittedAt = useMemo(() => new Date(), []);

  const referenceId = quoteResult?.quoteId;

  return (
    <div
      className={cn(
        "apex-flex apex-flex-col apex-items-center apex-justify-center apex-min-h-[400px] apex-p-8 apex-text-center",
        className
      )}
    >
      {/* Animated Checkmark + Confetti */}
      <div className="apex-relative apex-mb-6">
        {CONFETTI.map((dot, i) => (
          <span
            key={i}
            className="apex-confetti-dot"
            style={{
              "--confetti-x": dot.x,
              "--confetti-y": dot.y,
              backgroundColor: dot.color,
              top: "50%",
              left: "50%",
              marginTop: "-3px",
              marginLeft: "-3px",
            } as React.CSSProperties}
          />
        ))}

        <svg width="72" height="72" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle
            cx="26"
            cy="26"
            r="24"
            stroke="hsl(var(--apex-success))"
            strokeWidth="2.5"
            fill="hsl(var(--apex-success-light))"
            className="apex-check-circle"
          />
          <path
            d="M16 27l6 6 14-14"
            stroke="hsl(var(--apex-success))"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className="apex-check-mark"
          />
        </svg>
      </div>

      {/* Title — role=status so screen readers announce success (#539) */}
      <h2
        role="status"
        aria-live="polite"
        className="apex-text-2xl apex-font-bold apex-text-foreground apex-mb-2 apex-font-display apex-animate-fade-in-up apex-delay-300"
      >
        Quote Request Submitted!
      </h2>

      {/* Message */}
      <p className="apex-text-muted-foreground apex-mb-4 apex-max-w-md apex-animate-fade-in-up apex-delay-400">
        {quoteResult?.message ||
          "Thank you for your interest. We've received your quote request and will be in touch shortly."}
      </p>

      {/* Reference number (#539) — selectable text so the customer can copy/keep it */}
      {referenceId && (
        <div
          className="apex-mb-6 apex-px-4 apex-py-2.5 apex-rounded-lg apex-border apex-border-primary/30 apex-bg-primary/5 apex-animate-fade-in-up apex-delay-450"
          data-testid="quote-reference"
        >
          <p className="apex-text-xs apex-text-muted-foreground apex-mb-0.5 apex-uppercase apex-tracking-wider">
            Reference
          </p>
          <p className="apex-text-sm apex-font-mono apex-font-semibold apex-text-foreground apex-select-all">
            Quote {referenceId}
          </p>
          <p className="apex-text-[11px] apex-text-muted-foreground apex-mt-0.5">
            Keep this number for any follow-ups.
          </p>
        </div>
      )}

      {/* Summary */}
      <div className="apex-bg-muted/50 apex-rounded-lg apex-p-4 apex-mb-6 apex-w-full apex-max-w-sm apex-text-left apex-animate-fade-in-up apex-delay-500">
        <h3 className="apex-text-sm apex-font-semibold apex-text-muted-foreground apex-uppercase apex-tracking-wider apex-mb-3">
          Request Summary
        </h3>

        {selectedRootItem && (
          <div className="apex-flex apex-justify-between apex-py-1">
            <span className="apex-text-muted-foreground">Product</span>
            <span className="apex-font-medium apex-text-foreground">
              {selectedRootItem.name || selectedRootItem.code}
            </span>
          </div>
        )}

        {quoteForm.name && (
          <div className="apex-flex apex-justify-between apex-py-1">
            <span className="apex-text-muted-foreground">Name</span>
            <span className="apex-font-medium apex-text-foreground">
              {quoteForm.name}
            </span>
          </div>
        )}

        {quoteForm.email && (
          <div className="apex-flex apex-justify-between apex-py-1">
            <span className="apex-text-muted-foreground">Email</span>
            <span className="apex-font-medium apex-text-foreground">
              {quoteForm.email}
            </span>
          </div>
        )}
      </div>

      {/* What's Next — Timeline (themable per theme.confirmationCopy) */}
      <div className="apex-mb-6 apex-w-full apex-max-w-sm apex-animate-fade-in-up apex-delay-600">
        <p className="apex-font-semibold apex-text-foreground apex-mb-4 apex-font-display">What happens next?</p>
        <div className="apex-space-y-3 apex-text-left">
          {timelineSteps.map((step, i) => (
            <div key={i} className="apex-flex apex-items-start apex-gap-3">
              <div className="apex-shrink-0 apex-w-7 apex-h-7 apex-rounded-full apex-bg-primary/10 apex-text-primary apex-flex apex-items-center apex-justify-center apex-text-xs apex-font-bold">
                {i + 1}
              </div>
              <p className="apex-text-sm apex-text-muted-foreground apex-pt-1">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTAs (#539) — Configure another + optional Visit our website */}
      <div className="apex-flex apex-flex-wrap apex-items-center apex-justify-center apex-gap-3 apex-animate-fade-in-up apex-delay-700">
        <Button onClick={reset} size="lg" aria-label="Configure another product">
          Configure another
        </Button>
        {websiteUrl && (
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit our website (opens in a new tab)"
            className="apex-inline-flex apex-items-center apex-gap-1.5 apex-h-12 apex-px-5 apex-text-base apex-font-medium apex-text-primary apex-border apex-border-primary/30 apex-rounded-md hover:apex-bg-primary/5 apex-transition-colors"
          >
            Visit our website
            <ExternalLinkIcon />
          </a>
        )}
      </div>

      {/* Submission footer (#539) — small line under the CTAs */}
      {quoteForm.email && (
        <p className="apex-text-xs apex-text-muted-foreground apex-mt-6 apex-animate-fade-in-up apex-delay-700">
          Submitted from <span className="apex-font-medium">{quoteForm.email}</span> on{" "}
          {submittedAt.toLocaleDateString()} at {submittedAt.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}
